import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Observable, filter, map, fromEvent } from 'rxjs';
import { Router } from '@angular/router';

/**
 * Comprehensive cache management service for Angular PWA
 * Implements multiple cache clearing strategies for reliable updates across all devices and browsers
 */
@Injectable({
  providedIn: 'root'
})
export class SiteCacheService {
  private swUpdate = inject(SwUpdate);
  private router = inject(Router);
  
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  public updateAvailable$ = this.updateAvailableSubject.asObservable();
  
  private currentVersionSubject = new BehaviorSubject<string>('unknown');
  public currentVersion$ = this.currentVersionSubject.asObservable();

  private unrecoverableSubject = new BehaviorSubject<any>(null);
  public unrecoverable$ = this.unrecoverableSubject.asObservable().pipe(
    filter(event => event !== null)
  );

  private isUpdating = false;
  private criticalCacheValidated = false;

  constructor() {
    this.initializeUpdateChecking();
    this.setupVisibilityChangeListener();
    this.validateCriticalCacheSafely();
  }

  private initializeUpdateChecking(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // Listen for version updates
    this.swUpdate.versionUpdates.subscribe(event => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          break;
        case 'VERSION_READY':
          this.updateAvailableSubject.next(true);
          this.updateCurrentVersion(event.currentVersion.hash);
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error('[SiteCacheService] Version installation failed:', event.error);
          // On installation failure, try to clear everything and reload
          this.performCompleteCacheFlush();
          break;
      }
    });

    // Handle unrecoverable states with aggressive cache clearing
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('[SiteCacheService] Unrecoverable state:', event.reason);
      this.unrecoverableSubject.next(event);
      this.performCompleteCacheFlush();
    });

    // Check for updates when the app becomes visible again
    this.setupVisibilityChangeListener();
  }

  /**
   * Setup listener for when app becomes visible (user returns to tab)
   * This helps catch updates that might have been missed
   */
  private setupVisibilityChangeListener(): void {
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange').subscribe(() => {
        if (!document.hidden && this.swUpdate.isEnabled) {
          this.checkForUpdate();
        }
      });
    }
  }

  /**
   * Check for updates manually with enhanced error handling
   */
  public async checkForUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled || this.isUpdating) {
      return false;
    }

    try {
      const updateFound = await this.swUpdate.checkForUpdate();
      return updateFound;
    } catch (error) {
      console.error('[SiteCacheService] Failed to check for update:', error);
      // If check fails, try a complete cache flush
      this.performCompleteCacheFlush();
      return false;
    }
  }

  /**
   * Apply available update with comprehensive cache clearing
   */
  public async applyUpdate(): Promise<void> {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;

    try {
      
      if (this.swUpdate.isEnabled) {
        await this.swUpdate.activateUpdate();
      }
      
      // Perform complete cache flush for reliability
      await this.performCompleteCacheFlush();
    } catch (error) {
      console.error('[SiteCacheService] Failed to activate update:', error);
      // Fallback to complete cache flush
      await this.performCompleteCacheFlush();
    }
  }

  /**
   * Comprehensive cache flush that clears all possible caches
   * This is the nuclear option for ensuring users get fresh content
   */
  public async performCompleteCacheFlush(): Promise<void> {

    try {
      // Step 1: Clear all Cache API caches
      await this.clearAllCaches();

      // Step 2: Clear browser storage
      this.clearBrowserStorage();

      // Step 3: Unregister service worker
      await this.unregisterServiceWorker();

      // Step 4: Force reload with cache busting
      this.forceReloadWithCacheBust();
    } catch (error) {
      console.error('[SiteCacheService] Error during cache flush:', error);
      // Last resort: simple reload
      window.location.reload();
    }
  }

  /**
   * Clear all Cache API caches
   */
  private async clearAllCaches(): Promise<void> {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        
        await Promise.all(
          cacheNames.map(cacheName => {
            return caches.delete(cacheName);
          })
        );
        
      } catch (error) {
        console.error('[SiteCacheService] Error clearing caches:', error);
      }
    }
  }

  /**
   * Clear browser storage (localStorage, sessionStorage, indexedDB)
   */
  private clearBrowserStorage(): void {
    try {
      // Clear localStorage except for essential items
      const essentialKeys = ['user_preferences', 'auth_token', 'duna_template_state'];
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !essentialKeys.includes(key)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      // Clear app version info to force fresh detection
      localStorage.removeItem('app_current_version');
      
      // Clear sessionStorage
      sessionStorage.clear();
      
    } catch (error) {
      console.error('[SiteCacheService] Error clearing browser storage:', error);
    }
  }

  /**
   * Unregister service worker completely
   */
  private async unregisterServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        
        await Promise.all(
          registrations.map(registration => {
            return registration.unregister();
          })
        );
        
      } catch (error) {
        console.error('[SiteCacheService] Error unregistering service workers:', error);
      }
    }
  }

  /**
   * Force reload with cache busting parameters
   */
  private forceReloadWithCacheBust(): void {
    const currentUrl = new URL(window.location.href);
    
    // Add cache-busting parameters
    currentUrl.searchParams.set('_t', Date.now().toString());
    currentUrl.searchParams.set('_v', Math.random().toString(36));
    currentUrl.searchParams.set('_nocache', '1');
    
    
    // Use location.replace to avoid adding to history
    window.location.replace(currentUrl.href);
  }

  /**
   * Simple app reload without cache manipulation
   */
  public reloadApp(): void {
    window.location.reload();
  }

  /**
   * Get current app version info
   */
  public getCurrentVersion(): Observable<string> {
    return this.swUpdate.versionUpdates.pipe(
      filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
      map(event => event.currentVersion.hash)
    );
  }

  /**
   * Get current version from service worker or fallback
   */
  public getCurrentVersionSync(): string {
    const stored = localStorage.getItem('app_current_version');
    return stored || 'unknown';
  }

  /**
   * Update the current version in storage
   */
  private updateCurrentVersion(hash: string): void {
    localStorage.setItem('app_current_version', hash);
    this.currentVersionSubject.next(hash);
  }

  /**
   * Get home URL without cache-busting parameters
   */
  public getHomeUrl(): string {
    return window.location.origin;
  }

  /**
   * Navigate to home with complete cache flush
   */
  public async navigateHomeWithCacheFlush(): Promise<void> {
    
    try {
      // Step 1: Clear all Cache API caches
      await this.clearAllCaches();

      // Step 2: Clear browser storage
      this.clearBrowserStorage();

      // Step 3: Unregister service worker
      await this.unregisterServiceWorker();

      // Step 4: Navigate to home cleanly without cache-busting parameters
      this.router.navigate(['/'], { replaceUrl: true });
      
    } catch (error) {
      console.error('[SiteCacheService] Error during cache flush:', error);
      // Fallback: navigate to home
      this.router.navigate(['/'], { replaceUrl: true });
    }
  }

  /**
   * Check if we're running in a supported environment
   */
  public isSupported(): boolean {
    return 'serviceWorker' in navigator && 'caches' in window;
  }

  /**
   * Get detailed cache information for debugging
   */
  public async getCacheInfo(): Promise<any> {
    if (!this.isSupported()) {
      return { supported: false };
    }

    try {
      const cacheNames = await caches.keys();
      const cacheInfo = await Promise.all(
        cacheNames.map(async (name) => {
          const cache = await caches.open(name);
          const keys = await cache.keys();
          return { name, urls: keys.map(req => req.url) };
        })
      );

      return {
        supported: true,
        swEnabled: this.swUpdate.isEnabled,
        currentVersion: this.getCurrentVersionSync(),
        caches: cacheInfo
      };
         } catch (error) {
       console.error('[SiteCacheService] Error getting cache info:', error);
       return { supported: true, error: error instanceof Error ? error.message : String(error) };
     }
  }

  /**
   * Validate that the cached shell contains real HTML; if not, flush caches to avoid blank page from empty cache.
   */
  private validateCriticalCacheSafely(): void {
    if (this.criticalCacheValidated) {
      return;
    }

    if (typeof window === 'undefined' || !this.isSupported()) {
      return;
    }

    this.criticalCacheValidated = true;

    // Run asynchronously to avoid blocking startup
    setTimeout(() => {
      this.validateCriticalCache().catch(err => {
        console.error('[SiteCacheService] Error during critical cache validation:', err);
      });
    }, 0);
  }

  private async validateCriticalCache(): Promise<void> {
    try {
      const cacheNames = await caches.keys();
      if (!cacheNames.length) {
        return; // Nothing cached yet
      }

      const criticalUrls = [
        `${window.location.origin}/`,
        `${window.location.origin}/index.html`
      ];

      let validHtmlFound = false;
      let invalidHtmlFound = false;

      for (const name of cacheNames) {
        const cache = await caches.open(name);

        for (const url of criticalUrls) {
          const match = await cache.match(url);
          if (!match) {
            continue;
          }

          if (await this.isHtmlResponseUsable(match)) {
            validHtmlFound = true;
            break;
          } else {
            invalidHtmlFound = true;
          }
        }

        if (validHtmlFound) {
          break;
        }
      }

      // If we found only invalid/empty HTML, clear everything to avoid white page
      if (!validHtmlFound && invalidHtmlFound) {
        console.warn('[SiteCacheService] Cached app shell is invalid/empty, flushing caches to avoid blank page');
        await this.performCompleteCacheFlush();
      }
    } catch (error) {
      console.error('[SiteCacheService] Failed to validate critical cache:', error);
    }
  }

  private async isHtmlResponseUsable(response: Response): Promise<boolean> {
    try {
      if (!response || response.status !== 200) {
        return false;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/html')) {
        return false;
      }

      const text = await response.clone().text();
      // Treat very small or empty documents as invalid
      const minLength = 200;
      return !!text && text.trim().length >= minLength;
    } catch {
      return false;
    }
  }
}
